import { MoneyDisplay } from "@/components/accounting/money-display";
import { Button } from "@/components/ui/button";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import type { CounterpartyReconLine } from "@/gen/omnios/v1/accounting_pb";
import { useGetCounterpartyRecon } from "@/hooks/use-accounting";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { useAccountingContext } from "@/lib/accounting-context";
import { formatDate } from "@/lib/format";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useGridLayout } from "@/lib/use-grid-layout";

import type { ColDef, ValueFormatterParams } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { Input } from "@/components/ui/input";

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/accounting/counterparty-recon")({
	component: CounterpartyReconPage,
});

function formatAmount(value: number): string {
	return new Intl.NumberFormat("ru-RU", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(value);
}

function CounterpartyReconPage() {
	const { t } = useTranslation("accounting");
	const navigate = useNavigate();
	const gridRef = useRef<AgGridReact>(null);
	const { onGridReady, onStateChanged } = useGridLayout("counterparty-recon");
	const { periodId } = useAccountingContext();

	useBreadcrumbs([
		{
			label: t("counterparty_recon", {
				defaultValue: "Акт сверки",
			}),
		},
	]);

	const [counterpartyId, setCounterpartyId] = useState("");
	const [accountCode, setAccountCode] = useState("62");

	const { data, isLoading } = useGetCounterpartyRecon(
		counterpartyId,
		periodId,
		accountCode,
	);

	const columnDefs = useMemo<ColDef<CounterpartyReconLine>[]>(
		() => [
			{
				field: "date",
				headerName: t("entry_date"),
				width: 100,
				valueFormatter: (p: ValueFormatterParams<CounterpartyReconLine>) => {
					const d = p.data?.date;
					return d ? formatDate(d.toDate(), "dd.MM.yyyy") : "—";
				},
			},
			{
				field: "documentNumber",
				headerName: t("document_number", { defaultValue: "Документ" }),
				width: 110,
				cellClass: "font-mono text-xs",
			},
			{
				field: "description",
				headerName: t("entry_description"),
				flex: 1,
				minWidth: 200,
			},
			{
				field: "debit",
				headerName: t("line_debit"),
				width: 130,
				type: "rightAligned",
				cellClass: "font-tabular",
				valueGetter: (p) =>
					Number.parseFloat(p.data?.debit || "0") || null,
				valueFormatter: (p: ValueFormatterParams) =>
					p.value ? formatAmount(p.value) : "",
			},
			{
				field: "credit",
				headerName: t("line_credit"),
				width: 130,
				type: "rightAligned",
				cellClass: "font-tabular",
				valueGetter: (p) =>
					Number.parseFloat(p.data?.credit || "0") || null,
				valueFormatter: (p: ValueFormatterParams) =>
					p.value ? formatAmount(p.value) : "",
			},
			{
				field: "balance",
				headerName: t("running_balance"),
				width: 140,
				type: "rightAligned",
				cellClass: "font-tabular font-medium",
				valueGetter: (p) =>
					Number.parseFloat(p.data?.balance || "0"),
				valueFormatter: (p: ValueFormatterParams) =>
					formatAmount(p.value ?? 0),
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			filter: true,
			filterParams: {
				buttons: ["apply", "reset"],
				closeOnApply: true,
			},
		}),
		[],
	);

	const pinnedBottomRowData = useMemo(() => {
		if (!data?.lines.length) return [];
		return [
			{
				date: null,
				documentNumber: t("total"),
				description: "",
				debit: data.totalDebit,
				credit: data.totalCredit,
				balance: data.closingBalance,
				entryId: "",
			} as unknown as CounterpartyReconLine,
		];
	}, [data, t]);

	const onCellClicked = useCallback(
		(e: { colDef: ColDef; data: CounterpartyReconLine | undefined }) => {
			if (e.data?.entryId) {
				navigate({
					to: "/accounting/journal/$id",
					params: { id: e.data.entryId },
				});
			}
		},
		[navigate],
	);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "period",
				content: `${t("period")}: ${periodId}`,
				align: "left" as const,
			},
			...(counterpartyId
				? [
						{
							id: "counterparty",
							content: `${t("counterparty", { defaultValue: "Контрагент" })}: ${data?.counterpartyName ?? counterpartyId}`,
							align: "left" as const,
						},
					]
				: []),
		],
		[periodId, counterpartyId, data, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<div className="flex flex-col h-full overflow-hidden">
			{/* Controls bar */}
			<div
				className="flex items-center gap-3 px-2 border-b shrink-0"
				style={{ height: 32 }}
			>
				<span className="text-xs text-muted-foreground">
					{t("counterparty", { defaultValue: "Контрагент" })}:
				</span>
				<Input
					type="text"
					value={counterpartyId}
					onChange={(e) => setCounterpartyId(e.target.value)}
					placeholder={t("counterparty_placeholder", {
						defaultValue: "Введите ID контрагента",
					})}
					className="h-6 text-xs border rounded px-2 w-48 bg-transparent outline-none focus:border-primary"
				/>
				<div className="h-4 w-px bg-border" />
				<span className="text-xs text-muted-foreground">
					{t("account_code")}:
				</span>
				<Input
					type="text"
					value={accountCode}
					onChange={(e) => setAccountCode(e.target.value)}
					placeholder="62"
					className="h-6 text-xs border rounded px-2 w-16 font-mono bg-transparent outline-none focus:border-primary"
				/>
				<div className="flex-1" />
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => window.print()}
				>
					{t("toolbar_print")}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						gridRef.current?.api?.exportDataAsCsv({
							fileName: `recon_${counterpartyId}_${periodId}.csv`,
						})
					}
				>
					CSV
				</Button>
			</div>

			{/* Balance summary */}
			{data && (
				<div className="grid grid-cols-3 gap-px bg-border border-b shrink-0">
					<div className="bg-background px-3 py-1.5">
						<p className="text-[10px] text-muted-foreground">
							{t("opening_balance")}
						</p>
						<MoneyDisplay
							amount={data.openingBalance}
							className="text-sm font-semibold"
						/>
					</div>
					<div className="bg-background px-3 py-1.5">
						<p className="text-[10px] text-muted-foreground">
							{t("turnover")}
						</p>
						<span className="text-sm tabular-nums">
							{t("line_debit")}:{" "}
							{formatAmount(Number.parseFloat(data.totalDebit || "0"))}
							{" / "}
							{t("line_credit")}:{" "}
							{formatAmount(Number.parseFloat(data.totalCredit || "0"))}
						</span>
					</div>
					<div className="bg-background px-3 py-1.5">
						<p className="text-[10px] text-muted-foreground">
							{t("closing_balance")}
						</p>
						<MoneyDisplay
							amount={data.closingBalance}
							className="text-sm font-semibold"
							colorize
						/>
					</div>
				</div>
			)}

			{/* Grid */}
			{!counterpartyId ? (
				<div className="flex-1 flex items-center justify-center">
					<p className="text-sm text-muted-foreground">
						{t("select_counterparty", {
							defaultValue: "Введите контрагента для формирования акта сверки",
						})}
					</p>
				</div>
			) : (
				<div
					className="flex-1 ag-theme-quartz"
					style={{ width: "100%" }}
				>
					<AgGridReact<CounterpartyReconLine>
						ref={gridRef}
						rowData={data?.lines ?? []}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						localeText={AG_GRID_LOCALE_RU}
						onGridReady={onGridReady}
						onSortChanged={onStateChanged}
						onFilterChanged={onStateChanged}
						onColumnMoved={onStateChanged}
						onColumnResized={onStateChanged}
						onColumnVisible={onStateChanged}
						rowHeight={28}
						headerHeight={32}
						animateRows={false}
						getRowId={(p) =>
							`${p.data.entryId}-${p.data.debit}-${p.data.credit}`
						}
						loading={isLoading}
						pinnedBottomRowData={pinnedBottomRowData}
						onCellClicked={onCellClicked}
						overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("empty_recon_title", { defaultValue: "Нет операций" })}</p></div>`}
					/>
				</div>
			)}
		</div>
	);
}
